import axios from "axios";
import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";

import { ShareData } from "../types/type";

import HeroSection from "../components/HeroSection";
import FileUploadedItem from "../components/FileUploadedItem";
import { getAxiosErrorMessage } from "../utils/funcErrorMessageCatch";
import { toast, ToastContainer } from "react-toastify";
import { ENDPOINT_GENERATE_DOWNLOAD, ENDPOINT_GET_SHARE_BY_UID } from "../api/urls";
import { useTranslation } from "react-i18next";


function ShareScreen() {
  const { t, i18n } = useTranslation();

  const [searchParams] = useSearchParams();

  const uid: string = searchParams.get("uid")!;
  const token: string = searchParams.get("token")!;

  const [loading, setLoading] = useState(false);
  const [download, setDownload] = useState(false);
  const [share, setShare] = useState<ShareData | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    axios.get<ShareData>(
      `${ENDPOINT_GET_SHARE_BY_UID}/${uid}`,
      { headers: { Authorization: `Bearer ${token}` }}
    )
      .then(res => {
        if (res.status === 200) {
          setShare(res.data);
        } else {
          setNotFound(true);
          setLoading(false);

        }
      })
      .catch(err => {
        console.error(err);
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [uid, token]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen w-screen bg-black">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>;
  }

  const changeLanguage = (e: React.ChangeEvent<HTMLSelectElement>) => {
      i18n.changeLanguage(e.target.value);
  };


  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    try {
      setDownload(true);

      const response = await axios.get(
        `${ENDPOINT_GENERATE_DOWNLOAD}?share_uid=${uid}&token=${token}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { url } = response.data;

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${share?.name.replace(/\s+/g, '-')}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      toast.error(`${t("key_error_toast_download")} ! ${getAxiosErrorMessage(error)}`);
    } finally {
      setDownload(false);
    }
  };


  return (
    <main className="min-h-screen flex max-sm:flex-col md:flex-row gap-20 bg-cover bg-center md:p-20 max-sm:p-10 text-white"  style={{ backgroundImage: "url('/images/bg.jpg')" }}>
      
      <select className="absolute right-4 top-4 bg-transparent outline-none flex gap-2" value={i18n.language} onChange={changeLanguage}>
        <option value="fr" className="text-black">FR</option>
        <option value="en" className="text-black">EN</option>
      </select>

      {/* Section Information */}
      <section className='flex-1 md:sticky top-0 min-h-[60vh] md:w-1/2'>
        <HeroSection/>
      </section>
      
      {/* Section Upload */}
      <section className='flex-1 md:w-1/2 bg-yellow-30'>

        {/* Block ToastConiatner */}
        <ToastContainer  position="top-right" autoClose={5000} hideProgressBar={true} newestOnTop={false}
        closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored"/>

      { (notFound || !share) ? <div className="lg:px-[10%] md:px-2 flex flex-col items-start gap-10">
          <p className="text-xl">{t("key_share_text_not_exist")}</p>
          <Link to="/" className={`flex items-center text-blue-600 bg-blue-60 hover:underline font-bold text-xl`}>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t("key_share_link_go_home")}
          </Link>
        </div> : <div className='lg:px-[10%] md:px-2 flex flex-col gap-10'>
          <div className="flex flex-col">
            <p>{t("key_share_title")}{`${share?.files?.length > 0 ? 's':''}`}</p>
          </div>

          <div className="bg-green-30 flex flex-col gap-3 h-[30vh overflow-y-aut">
            { share.files.map(file => <FileUploadedItem file={file} key={file.uid}/> )}
            
            <button type="button" onClick={handleDownload} disabled={loading}
              className={`w-full flex justify-center outline-none items-center gap-4 text-center rounded-lg p-4 text-white bg-blue-600 hover:bg-blue-700 font-bold text-sm mt-4`}>
              {t("key_share_btn_download")}
              { download && <span className="animate-spin rounded-full h-4 w-4 border-b-4 border-white"/>}
            </button>
          </div>
        </div>
      }
        
      </section>
    </main>
  );
}

export default ShareScreen;
